import React, { ReactElement } from "react";

import InfiniteContentScroller from "../infinite-content-scroller/infinite-content-scroller";
import Spinner from "../spinner/spinner";
import Icons from "../../icons/icons";
import HttpService from "../../../services/http.service";
import { StorageContext } from "../../storage/storage-context";
import { ModalContext } from "../../modal/modal";
import { Theme } from "../../../models/models";

export interface SCRUDFormComponent<T> { 
  initModel?: T, 
  onSubmit: (m: T, ...args: (any | undefined)[]) => void, 
  onCancel: (...args: (any | undefined)[]) => void, 
  [prop: string]: any | undefined 
};
export interface SCRUDRenderComponent<T> { 
  initModel: T, 
  [prop: string]: any | undefined 
};

type SCRUDProps<T> = {
  css?: {
    searchbar?: string,
    newbutton?: string,
    componentWrapper?: string
  },
  Components: {
    ModelRender: React.FC<SCRUDRenderComponent<T>>,
    ModelForm: React.FC<SCRUDFormComponent<T>>
  },
  routes: {
    stream: {
      routeName: string,
      params?: (content: T[]) => any
    }
    create: {
      routeName: string,
      params?: (model: Partial<T>) => any
    }
    update: {
      routeName: string,
      params?: (model: Partial<T>) => any
    }
    delete: {
      routeName: string,
      params?: (model: Partial<T>) => any
    }
  },
  content: T[],
  setContent: React.Dispatch<React.SetStateAction<T[]>>,
  busyElement?: React.ReactElement,
  newDummyElement: T
}

const SCRUD = <T extends { id: number }>({
  css, Components, routes, content, setContent, busyElement, newDummyElement
}: SCRUDProps<T>): ReactElement => {

  const [storageContext] = React.useContext(StorageContext);
  const modalContext = React.useContext(ModalContext);
  const [editContentAtIndex, setEditContentAtIndex] = React.useState<number | null>(null);
  const [search, setSearch] = React.useState<string>(''); // to search artist profiles

  return (
    <div>

      <div className="flex pb-3 w-full justify-center items-center">
        <input 
          type="text" 
          className={` grow ${css?.searchbar} m-0 h-12`} 
          placeholder="Search.."
          onChange={e => setSearch(e.target.value)}
        />
        <button 
          className={`btn btn-primary flex items-center justify-center !m-0 grow-0 ${css?.newbutton}`}
          onClick={() => {
            setContent(ctnt => ([
              {
                ...newDummyElement,
                id: -1,
              },
              ...ctnt
            ]));
            setEditContentAtIndex(0);
          }}
        >
          <span className="w-6 h-6 inline-flex justify-center items-center">{Icons.plus}</span>
          &nbsp;New
        </button>
      </div>

      <InfiniteContentScroller
        contentStreamingRoute={routes.stream.routeName}
        contentSetter={setContent}
        content={content}
        search={search}
        params={routes.stream.params?.(content)}
        busyElement={
          busyElement ||
          (
            <div className="h-[60vh] w-[50vw] flex flex-col items-center justify-center">
              <Spinner/>
              <p className="neon-text font-black text-teal-400 text-lg">Loading profiles..</p>
            </div>
          )
        }
      >
        <div className="py-4">
          {
            content.map((c, i) => (
              (i === editContentAtIndex) ?
                <div className={`relative my-1 ${css?.componentWrapper || ''}`} key={i}>
                  <Components.ModelForm 
                    initModel={{...c}}
                    onCancel={() => {
                      if (c.id === -1) {
                        setContent(cnt => cnt.slice(1));
                      } 
                      setEditContentAtIndex(null);
                    }}
                    onSubmit={async (updatedContent: any) => {
                      const updateRes = (c.id === -1) ? 
                        await HttpService.post(routes.create.routeName, routes.create.params?.(updatedContent))
                      :
                        await HttpService.patch(routes.update.routeName, routes.update.params?.(updatedContent));
                      if (updateRes.success) {
                        modalContext.toast!('success', updateRes.messages[0]);
                        setContent([]);
                        setEditContentAtIndex(null);
                      } else {
                        modalContext.toast!('warning', updateRes.messages[0]);
                        console.log(updateRes.messages);
                      }
                    }}
                  />
                </div>
              :
                <div className={`relative my-1 cursor-pointer  ${css?.componentWrapper || ''}`} key={i}>
                  <Components.ModelRender initModel={c}/>
                  <div 
                    className="absolute top-0 right-0 w-12 h-12 bg-red-400 flex items-center justify-center rounded-full translate-x-1/2 -translate-y-1/2"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const modalRes = await modalContext.modal!({ prompt: `Are you sure you want to delete?`, options: ['Yes', 'Cancel']});
                      if (modalRes === 'Yes') {
                        const delRes = await HttpService.delete(routes.delete.routeName, routes.delete.params?.(c));
                        if (delRes.success) {
                          modalContext.toast!('success', delRes.messages[0]);
                          setContent([]);
                        } else {
                          modalContext.toast!('warning', delRes.messages[0]);
                          console.log(delRes.messages);
                        }
                      }
                    }}
                  >
                    <div className="p-3 text-black">{Icons.trash}</div>
                  </div>
                  <div 
                    className="absolute top-0 right-14 w-12 h-12 bg-orange-400 flex items-center justify-center rounded-full translate-x-1/2 -translate-y-1/2"
                    onClick={() => {
                      if (editContentAtIndex === 0 && content[0].id === -1) {
                        setContent(ct => ct.slice(1));
                      }
                      setEditContentAtIndex(i) 
                    }}
                  >
                    <div className="p-3 text-black">{Icons.pencil}</div>
                  </div>
                </div>
            ))
          }
        </div>
      </InfiniteContentScroller>
    </div>
  )
}

export default SCRUD;
